import theme from '../../theme';
import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { getRandomSuggestions } from './suggestions';
import PageHeader from '../PageHeader';
import { useOptionalAuth } from '../../lib/useOptionalAuth';
import { LEARN_SUBDOMAIN_URL } from '../../lib/subdomain';

const API_URL = process.env.REACT_APP_API_URL;

// ── Styles ────────────────────────────────────────────────────────────────────

const s = {
  container: {
    minHeight: '100vh',
    backgroundColor: theme.bgDark,
    fontFamily: 'Georgia, serif',
    display: 'flex',
    flexDirection: 'column',
    color: theme.textPrimary,
  },
  header: {
    padding: '14px 24px',
    borderBottom: '1px solid rgba(184, 149, 90, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.bgDark,
    position: 'sticky',
    top: '52px',
    zIndex: 10,
  },
  title: {
    fontSize: '15px',
    fontWeight: '600',
    color: theme.gold,
    margin: 0,
  },
  subtitle: {
    fontSize: '12px',
    color: theme.textMuted,
    margin: '2px 0 0',
  },
  chatArea: {
    flex: 1,
    padding: '28px 24px',
    maxWidth: '720px',
    width: '100%',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    boxSizing: 'border-box',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingTop: '60px',
    gap: '12px',
  },
  emptyText: {
    fontSize: '14px',
    color: theme.textMuted,
    fontStyle: 'italic',
    margin: 0,
  },
  suggestionChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    justifyContent: 'center',
    maxWidth: '720px',
    margin: '0 auto 8px',
    padding: '0 24px',
  },
  rowRight: { display: 'flex', justifyContent: 'flex-end' },
  rowLeft:  { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '6px' },
  qBubble: {
    backgroundColor: theme.bgCard,
    color: theme.textPrimary,
    border: '1px solid rgba(var(--ap-text-primary-rgb), 0.15)',
    padding: '10px 16px',
    borderRadius: '4px',
    fontSize: '14px',
    maxWidth: '75%',
    lineHeight: '1.6',
  },
  aBubble: {
    backgroundColor: theme.bgCard,
    border: '1px solid rgba(var(--ap-text-primary-rgb), 0.18)',
    padding: '14px 18px',
    borderRadius: '4px',
    fontSize: '14px',
    maxWidth: '85%',
    lineHeight: '1.8',
    color: theme.textPrimary,
  },
  thinkingBubble: {
    backgroundColor: theme.bgCard,
    border: '1px solid rgba(var(--ap-text-primary-rgb), 0.18)',
    padding: '12px 16px',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    backgroundColor: theme.textMuted,
  },
  inputArea: {
    borderTop: '1px solid rgba(184, 149, 90, 0.2)',
    padding: '14px 24px 16px',
    backgroundColor: theme.bgDark,
  },
  inputRow: {
    maxWidth: '720px',
    margin: '0 auto',
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
  },
  hintBtn: {
    padding: '10px 12px',
    backgroundColor: theme.bgCard,
    color: theme.gold,
    border: '1px solid rgba(184, 149, 90, 0.3)',
    borderRadius: '4px',
    fontSize: '16px',
    cursor: 'pointer',
    transition: 'background 0.15s',
    height: '42px',
  },
  textarea: {
    flex: 1,
    padding: '10px 14px',
    fontSize: '14px',
    border: '1px solid rgba(var(--ap-text-primary-rgb), 0.2)',
    borderRadius: '4px',
    resize: 'none',
    fontFamily: 'Georgia, serif',
    color: theme.textPrimary,
    lineHeight: '1.5',
    backgroundColor: theme.bgDark,
    boxSizing: 'border-box',
  },
  sendBtn: {
    padding: '10px 22px',
    backgroundColor: theme.gold,
    color: theme.bgDark,
    border: 'none',
    borderRadius: '4px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'opacity 0.15s',
    height: '42px',
  },
  sendBtnDisabled: {
    opacity: 0.35,
    cursor: 'not-allowed',
  },
  disclaimer: {
    textAlign: 'center',
    fontSize: '12px',
    color: theme.textMuted,
    margin: '16px auto 0',
    maxWidth: '720px',
  },
  quota: {
    textAlign: 'center',
    fontSize: '12px',
    color: theme.textMuted,
    margin: '12px auto 0',
    maxWidth: '720px',
  },
  quotaLink: {
    color: theme.gold,
    textDecoration: 'none',
  },
  sourcesContainer: {
    marginTop: '12px',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  sourcesTitle: {
    fontFamily: "'Josefin Sans', sans-serif",
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.12em',
    color: '#6cd6a0',
    fontWeight: '500',
    margin: '4px 0',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  verifiedTick: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '14px',
    height: '14px',
    borderRadius: '50%',
    backgroundColor: 'rgba(58, 170, 120, 0.15)',
    color: '#6cd6a0',
    fontSize: '12px',
    fontWeight: '700',
  },
};

const sug = {
  btn: {
    fontSize: '12px',
    color: theme.gold,
    background: theme.bgCard,
    border: '1px solid rgba(184, 149, 90, 0.3)',
    borderRadius: '4px',
    padding: '6px 14px',
    cursor: 'pointer',
    transition: 'background 0.15s',
    fontFamily: 'Georgia, serif',
  },
};

const CSS = `
  @keyframes bounce {
    0%, 80%, 100% { transform: translateY(0); }
    40% { transform: translateY(-6px); }
  }
  .dot { animation: bounce 1.2s infinite; }
  .dot:nth-child(2) { animation-delay: 0.2s; }
  .dot:nth-child(3) { animation-delay: 0.4s; }
  textarea:focus { border-color: ${theme.gold} !important; background: ${theme.bgCard} !important; outline: none; }
  .suggestion:hover { background: rgba(184, 149, 90, 0.1) !important; color: ${theme.gold} !important; }
  .send-btn:hover:not([disabled]) { opacity: 0.85; }
  .hint-btn:hover { background: rgba(184, 149, 90, 0.1) !important; }
  .answer-bubble p { margin: 0 0 0.4rem 0; }
  .answer-bubble p:last-child { margin-bottom: 0; }
  .answer-bubble ul, .answer-bubble ol { margin: 0.25rem 0 0.4rem 1.2rem; padding: 0; }
  .answer-bubble li { margin-bottom: 0.2rem; }
  .answer-bubble strong { color: ${theme.gold}; }
  .answer-bubble h1, .answer-bubble h2, .answer-bubble h3,
  .answer-bubble h4, .answer-bubble h5, .answer-bubble h6 {
    color: ${theme.gold};
    font-size: 1em;
    font-weight: 600;
    margin: 0.6rem 0 0.3rem;
  }
  .answer-bubble h1:first-child, .answer-bubble h2:first-child, .answer-bubble h3:first-child,
  .answer-bubble h4:first-child, .answer-bubble h5:first-child, .answer-bubble h6:first-child {
    margin-top: 0;
  }
  @keyframes tickBlink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.25; }
  }
  .verified-tick { animation: tickBlink 1s ease-in-out infinite; }

  /* ── Mobile (phone) overrides ──────────────────────────────────────────── */
  @media (max-width: 480px) {
    /* Hero: cut wasted vertical space above the suggested questions */
    .ask-header { padding-top: 16px !important; }
    .ask-header > div { margin-bottom: 12px !important; padding-bottom: 10px !important; }
    .ask-header h1 { font-size: 23px !important; margin-bottom: 4px !important; }
    .ask-header p { font-size: 13px !important; line-height: 1.45 !important; }
    .ask-header > div > div:last-child { gap: 8px !important; margin-top: 4px !important; }

    /* Chat area: tighter padding, reserve room for the fixed input bar below */
    .chat-area { padding: 12px 16px 160px !important; }
    .chat-area > div:first-child { padding-top: 12px !important; }

    /* Suggested questions: stack vertically, near-full width, comfortable taps */
    .suggestion-chips {
      flex-direction: column !important;
      align-items: stretch !important;
      width: 92% !important;
      max-width: 92% !important;
      margin: 0 auto 8px !important;
      padding: 0 !important;
      gap: 10px !important;
    }
    .suggestion {
      width: 100% !important;
      min-height: 44px !important;
      box-sizing: border-box !important;
      white-space: normal !important;
      text-align: left !important;
      padding: 12px 16px !important;
    }

    /* Input bar: sticky to the bottom, safe-area aware, compact row */
    .input-area {
      position: fixed !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      padding: 10px 12px calc(12px + env(safe-area-inset-bottom)) !important;
      z-index: 20 !important;
    }
    .input-row { gap: 8px !important; }
    .hint-btn { padding: 10px !important; }
    .chat-textarea {
      padding: 10px 12px !important;
      white-space: nowrap !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
    }
    .send-btn { padding: 10px 16px !important; }
  }
`;

// ── Component ─────────────────────────────────────────────────────────────────

export default function Chatbot() {
  const [messages, setMessages]         = useState([]);
  const [input, setInput]               = useState('');
  const [loading, setLoading]           = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions]   = useState(getRandomSuggestions);
  const [threadId, setThreadId]         = useState(() => sessionStorage.getItem('austrian_thread_id') || '');
  const [statusLabel, setStatusLabel]   = useState('Thinking');
  // What is left of today's questions. Shown up front rather than discovered by
  // running out: a visitor gets five a day, and finding that out by hitting a
  // wall mid-thought is a bad way to meet a limit.
  const [quota, setQuota]               = useState(null);
  const lastAnswerRef                   = useRef(null);
  const { isLoaded, isSignedIn, getToken } = useOptionalAuth();

  // Reloaded when sign-in settles, because the answer depends on it: the
  // allowance is keyed on the Clerk user when there is one, and on IP when
  // there is not.
  useEffect(() => {
    if (!isLoaded) return;
    let cancelled = false;
    (async () => {
      try {
        const token = isSignedIn ? await getToken().catch(() => null) : null;
        const res = await fetch(`${API_URL}/api/rag/quota`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setQuota(data);
      } catch {
        // Advisory only. If this fails the assistant still works; the server
        // decides the allowance either way.
      }
    })();
    return () => { cancelled = true; };
  }, [isLoaded, isSignedIn, getToken]);

  // Scroll to first line of answer when it arrives. Keyed on message count
  // (not the full array) so streaming token updates don't re-trigger this.
  useEffect(() => {
    const lastMsg = messages[messages.length - 1];
    if (!lastMsg || lastMsg.type !== 'a') return;
    setTimeout(() => {
      const el = lastAnswerRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY - 120;
      window.scrollTo({ top, behavior: 'smooth' });
    }, 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length]);

  const refreshSuggestions = () => {
    setSuggestions(getRandomSuggestions());
    setShowSuggestions(true);
  };

  // Appends a fresh SSE chunk to `buffer`, extracts every complete "\n\n"-terminated
  // frame, and returns { events, rest } -- `rest` is the leftover partial frame to
  // prepend next time (a frame can arrive split across multiple stream reads).
  const extractSSEEvents = (buffer) => {
    const events = [];
    let rest = buffer;
    let sepIndex;
    while ((sepIndex = rest.indexOf('\n\n')) !== -1) {
      const frame = rest.slice(0, sepIndex);
      rest = rest.slice(sepIndex + 2);
      let event = 'message';
      let dataLine = '';
      for (const line of frame.split('\n')) {
        if (line.startsWith('event:')) event = line.slice(6).trim();
        else if (line.startsWith('data:')) dataLine += line.slice(5).trim();
      }
      if (!dataLine) continue;
      try {
        events.push({ event, data: JSON.parse(dataLine) });
      } catch {
        // Malformed frame -- skip rather than crash the stream reader.
      }
    }
    return { events, rest };
  };

  const ask = async (question) => {
    if (!question.trim() || loading) return;
    const q = question.trim();
    setMessages(prev => [...prev, { type: 'q', text: q }, { type: 'a', text: '', sources: [], streaming: true }]);
    setInput('');
    setShowSuggestions(false);
    setStatusLabel('Thinking');
    setLoading(true);

    const updateAnswer = (updater) => {
      setMessages(prev => {
        const next = [...prev];
        next[next.length - 1] = updater(next[next.length - 1]);
        return next;
      });
    };

    try {
      const token = isSignedIn ? await getToken().catch(() => null) : null;
      const res = await fetch(`${API_URL}/api/rag/query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Sent when there is a session, so the server can tell an enrolled
          // student from a passer-by. Absent, the allowance falls back to IP.
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ question: q, threadId }),
        // Covers total streamed generation time (both provider attempts plus
        // retrieval), mirroring the gateway's own time-to-first-byte budget
        // plus headroom for a long answer.
        signal: AbortSignal.timeout(90000),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        // A 429 carries the server's own sentence, which explains the tier and
        // what enrolling would change. Showing a generic failure instead would
        // turn a comprehensible limit into an apparent outage.
        if (res.status === 429) setQuota((prev) => ({ ...(prev || {}), remaining: 0, exhausted: true }));
        updateAnswer(msg => ({ ...msg, text: data.error || 'Something went wrong. Please try again.', streaming: false }));
        return;
      }

      // The allowance rides in headers rather than the SSE body -- see
      // routes/rag.js. Absent when the quota store was unreachable.
      const remaining = res.headers.get('X-Rag-Remaining');
      if (remaining !== null) {
        setQuota((prev) => ({
          ...(prev || {}),
          tier: res.headers.get('X-Rag-Tier') || prev?.tier,
          limit: Number(res.headers.get('X-Rag-Limit')) || prev?.limit,
          remaining: Number(remaining),
          resetsAt: res.headers.get('X-Rag-Resets-At') || prev?.resetsAt,
        }));
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let newThreadId = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const { events, rest } = extractSSEEvents(buffer);
        buffer = rest;

        for (const { event, data } of events) {
          switch (event) {
            case 'status':
              setStatusLabel(data.stage === 'generating' ? 'Writing' : 'Retrieving sources');
              break;
            case 'sources':
              updateAnswer(msg => ({ ...msg, sources: data.sources || [] }));
              break;
            case 'token':
              updateAnswer(msg => ({ ...msg, text: msg.text + data.text }));
              break;
            case 'override':
              updateAnswer(msg => ({ ...msg, text: data.text }));
              break;
            case 'error':
              updateAnswer(msg => ({ ...msg, text: data.message || 'Something went wrong. Please try again.' }));
              break;
            case 'done':
              newThreadId = data.thread_id || data.threadId || null;
              break;
            default:
              break;
          }
        }
      }

      updateAnswer(msg => ({ ...msg, streaming: false }));
      if (newThreadId) {
        setThreadId(newThreadId);
        sessionStorage.setItem('austrian_thread_id', newThreadId);
      }
    } catch (err) {
      const text = err.name === 'TimeoutError'
        ? 'This is taking longer than expected. Please try again.'
        : 'Something went wrong. Please try again.';
      updateAnswer(msg => ({ ...msg, text, streaming: false }));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      ask(input);
    }
  };

  return (
    <div style={s.container}>
      <style>{CSS}</style>

      {/* Header */}
      <div className="ask-header" style={{ padding: '28px 24px 0', maxWidth: '720px', width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
        <PageHeader
          title="Ask Austrian"
          description="Explore Austrian Economics through grounded conversations backed by original sources."
        />
      </div>

      {/* Messages */}
      <div className="chat-area" style={s.chatArea}>
        {messages.length === 0 && !loading && (
          <div style={s.emptyState}>
            <p style={s.emptyText}>Ask anything about Austrian Economics</p>
            <div className="suggestion-chips" style={s.suggestionChips}>
              {suggestions.map((suggestion, i) => (
                <button key={i} className="suggestion" onClick={() => ask(suggestion)} style={sug.btn}>
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i}>
            {msg.type === 'q' && (
              <div style={s.rowRight}>
                <div style={s.qBubble}>{msg.text}</div>
              </div>
            )}
            {msg.type === 'a' && (
              <div style={s.rowLeft} ref={i === messages.length - 1 ? lastAnswerRef : null}>
                {msg.streaming && !msg.text ? (
                  <div style={s.thinkingBubble}>
                    <span style={{ color: theme.textMuted, fontSize: 13, fontStyle: 'italic' }}>{statusLabel}</span>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <div className="dot" style={s.dot} />
                      <div className="dot" style={s.dot} />
                      <div className="dot" style={s.dot} />
                    </div>
                  </div>
                ) : (
                  <div style={s.aBubble} className="answer-bubble">
                    <ReactMarkdown>{msg.text}</ReactMarkdown>
                  </div>
                )}
                {msg.sources && msg.sources.length > 0 && (
                  <div style={s.sourcesContainer}>
                    <div style={s.sourcesTitle}>
                      <span className="verified-tick" style={s.verifiedTick}>✓</span>
                      <span>Verified with sources</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Input */}
      <div className="input-area" style={s.inputArea}>
        {showSuggestions && (
          <div className="suggestion-chips" style={s.suggestionChips}>
            {suggestions.map((suggestion, i) => (
              <button key={i} className="suggestion" onClick={() => ask(suggestion)} style={sug.btn}>
                {suggestion}
              </button>
            ))}
          </div>
        )}
        <div className="input-row" style={s.inputRow}>
          <button className="hint-btn" onClick={refreshSuggestions} title="Suggest a question" style={s.hintBtn}>
            ?
          </button>
          <textarea
            className="chat-textarea"
            rows={1}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask a question... (Enter to send)"
            style={s.textarea}
            disabled={loading}
            aria-label="Chat query input"
          />
          <button
            className="send-btn"
            onClick={() => ask(input)}
            disabled={!input.trim() || loading}
            style={{ ...s.sendBtn, ...(!input.trim() || loading ? s.sendBtnDisabled : {}) }}
          >
            Ask
          </button>
        </div>
        {quota && Number.isFinite(quota.remaining) && (
          <p style={s.quota}>
            {quota.remaining > 0
              ? `${quota.remaining} of ${quota.limit} questions left today`
              : `No questions left today. Your allowance resets at midnight ${quota.timezone || 'UTC'}.`}
            {!quota.enrolled && (
              <>
                {' '}
                <a href={LEARN_SUBDOMAIN_URL} style={s.quotaLink}>
                  Enrolling raises it to 15 a day, free
                </a>.
              </>
            )}
          </p>
        )}
        <p style={s.disclaimer}>Ask Austrian responds from an educational and theoretical perspective. It does not provide investment advice, product recommendations, portfolio recommendations, or buy/sell/hold guidance.</p>
      </div>
    </div>
  );
}